import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import type {
  EPIType,
  EPIInventory,
  EPIAssignment,
  EPIExpiring,
  IncidentType,
  Incident,
  IncidentDetailed,
  IncidentSeverity,
  IncidentStatus,
  TrainingType,
  TrainingSession,
  TrainingAttendee,
  CertificationExpiring,
  RPLStats,
  EPICategory,
  EPIStatus,
  TrainingStatus,
  AttendanceStatus,
  ReturnCondition,
} from '@/types/rpl';

// ============== HELPERS ==============

export const getSeverityLabel = (severity: IncidentSeverity): string => {
  const labels: Record<IncidentSeverity, string> = {
    minor: 'Leve',
    moderate: 'Moderado',
    serious: 'Grave',
    critical: 'Crítico',
  };
  return labels[severity] || severity;
};

export const getSeverityColor = (severity: IncidentSeverity): string => {
  const colors: Record<IncidentSeverity, string> = {
    minor: 'bg-green-500/10 text-green-500',
    moderate: 'bg-yellow-500/10 text-yellow-500',
    serious: 'bg-orange-500/10 text-orange-500',
    critical: 'bg-red-500/10 text-red-500',
  };
  return colors[severity] || '';
};

export const getIncidentStatusLabel = (status: IncidentStatus): string => {
  const labels: Record<IncidentStatus, string> = {
    open: 'Abierto',
    investigating: 'En Investigación',
    closed: 'Cerrado',
    dismissed: 'Descartado',
  };
  return labels[status] || status;
};

export const getIncidentStatusColor = (status: IncidentStatus): string => {
  const colors: Record<IncidentStatus, string> = {
    open: 'bg-red-500/10 text-red-500',
    investigating: 'bg-yellow-500/10 text-yellow-500',
    closed: 'bg-green-500/10 text-green-500',
    dismissed: 'bg-gray-500/10 text-gray-500',
  };
  return colors[status] || '';
};

export const getEPIStatusLabel = (status: EPIStatus): string => {
  const labels: Record<EPIStatus, string> = {
    available: 'Disponible',
    assigned: 'Asignado',
    expired: 'Caducado',
    damaged: 'Dañado',
    disposed: 'Desechado',
  };
  return labels[status] || status;
};

export const getEPIStatusColor = (status: EPIStatus): string => {
  const colors: Record<EPIStatus, string> = {
    available: 'bg-green-500/10 text-green-500',
    assigned: 'bg-blue-500/10 text-blue-500',
    expired: 'bg-red-500/10 text-red-500',
    damaged: 'bg-orange-500/10 text-orange-500',
    disposed: 'bg-gray-500/10 text-gray-500',
  };
  return colors[status] || '';
};

export const getEPICategoryLabel = (category: EPICategory): string => {
  const labels: Record<EPICategory, string> = {
    head: 'Cabeza',
    hands: 'Manos',
    eyes: 'Ojos',
    feet: 'Pies',
    body: 'Cuerpo',
    respiratory: 'Respiratorio',
    hearing: 'Auditivo',
  };
  return labels[category] || category;
};

export const getTrainingStatusLabel = (status: TrainingStatus): string => {
  const labels: Record<TrainingStatus, string> = {
    scheduled: 'Programada',
    in_progress: 'En Curso',
    completed: 'Completada',
    cancelled: 'Cancelada',
  };
  return labels[status] || status;
};

export const getTrainingStatusColor = (status: TrainingStatus): string => {
  const colors: Record<TrainingStatus, string> = {
    scheduled: 'bg-blue-500/10 text-blue-500',
    in_progress: 'bg-yellow-500/10 text-yellow-500',
    completed: 'bg-green-500/10 text-green-500',
    cancelled: 'bg-gray-500/10 text-gray-500',
  };
  return colors[status] || '';
};

export const getAttendanceStatusLabel = (status: AttendanceStatus): string => {
  const labels: Record<AttendanceStatus, string> = {
    registered: 'Inscrito',
    attended: 'Asistió',
    absent: 'Ausente',
    excused: 'Justificado',
  };
  return labels[status] || status;
};

// ============== STATS ==============

export function useRPLStats() {
  return useQuery({
    queryKey: ['rpl-stats'],
    queryFn: async () => {
      const { data, error } = await api
        .from('rpl_stats')
        .select('*')
        .single();

      if (error) throw error;
      return data as RPLStats;
    },
  });
}

// ============== EPI TYPES ==============

export function useEPITypes(activeOnly = false) {
  return useQuery({
    queryKey: ['rpl-epi-types', activeOnly],
    queryFn: async () => {
      let query = api
        .from('rpl_epi_types')
        .select('*')
        .order('name');

      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as EPIType[];
    },
  });
}

export function useCreateEPIType() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: Partial<EPIType>) => {
      const { data: result, error } = await api
        .from('rpl_epi_types')
        .insert([data])
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rpl-epi-types'] });
      toast({ title: 'Tipo de EPI creado correctamente' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

export function useUpdateEPIType() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<EPIType> }) => {
      const { error } = await api
        .from('rpl_epi_types')
        .update(data)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rpl-epi-types'] });
      toast({ title: 'Tipo de EPI actualizado' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

// ============== EPI INVENTORY ==============

export function useEPIInventory(filters?: { epiTypeId?: string; status?: EPIStatus }) {
  return useQuery({
    queryKey: ['rpl-epi-inventory', filters],
    queryFn: async () => {
      let query = api
        .from('rpl_epi_inventory')
        .select('*, epi_type:rpl_epi_types(*)')
        .order('created_at', { ascending: false });

      if (filters?.epiTypeId) {
        query = query.eq('epi_type_id', filters.epiTypeId);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as EPIInventory[];
    },
  });
}

export function useCreateEPIInventory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: Partial<EPIInventory>) => {
      const { data: result, error } = await api
        .from('rpl_epi_inventory')
        .insert([data])
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rpl-epi-inventory'] });
      queryClient.invalidateQueries({ queryKey: ['rpl-stats'] });
      toast({ title: 'EPI registrado correctamente' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

export function useUpdateEPIInventory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<EPIInventory> }) => {
      const { error } = await api
        .from('rpl_epi_inventory')
        .update(data)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rpl-epi-inventory'] });
      queryClient.invalidateQueries({ queryKey: ['rpl-stats'] });
      toast({ title: 'EPI actualizado' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

// ============== EPI ASSIGNMENTS ==============

export function useEPIAssignments(filters?: { employeeId?: string; activeOnly?: boolean }) {
  return useQuery({
    queryKey: ['rpl-epi-assignments', filters],
    queryFn: async () => {
      let query = api
        .from('rpl_epi_assignments')
        .select(`
          *,
          epi_inventory:rpl_epi_inventory(*, epi_type:rpl_epi_types(*)),
          employee:profiles!rpl_epi_assignments_employee_id_fkey(id, full_name),
          assigned_by_profile:profiles!rpl_epi_assignments_assigned_by_fkey(id, full_name)
        `)
        .order('assigned_at', { ascending: false });

      if (filters?.employeeId) {
        query = query.eq('employee_id', filters.employeeId);
      }
      if (filters?.activeOnly) {
        query = query.is('returned_at', null);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as EPIAssignment[];
    },
  });
}

export function useEPIsExpiring() {
  return useQuery({
    queryKey: ['rpl-epis-expiring'],
    queryFn: async () => {
      const { data, error } = await api
        .from('rpl_epis_expiring')
        .select('*')
        .order('days_until_expiry');

      if (error) throw error;
      return data as EPIExpiring[];
    },
  });
}

export function useAssignEPI() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (data: {
      epi_inventory_id: string;
      employee_id: string;
      expected_return_date?: string;
      notes?: string;
    }) => {
      // Create assignment
      const { error: assignError } = await api
        .from('rpl_epi_assignments')
        .insert([{
          ...data,
          assigned_by: user?.id,
        }]);

      if (assignError) throw assignError;

      // Update inventory status
      const { error: updateError } = await api
        .from('rpl_epi_inventory')
        .update({ status: 'assigned' })
        .eq('id', data.epi_inventory_id);

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rpl-epi-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['rpl-epi-inventory'] });
      queryClient.invalidateQueries({ queryKey: ['rpl-stats'] });
      toast({ title: 'EPI asignado correctamente' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

export function useReturnEPI() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      assignmentId,
      inventoryId,
      condition,
      notes,
    }: {
      assignmentId: string;
      inventoryId: string;
      condition: ReturnCondition;
      notes?: string;
    }) => {
      // Update assignment
      const { error: assignError } = await api
        .from('rpl_epi_assignments')
        .update({
          returned_at: new Date().toISOString(),
          condition_on_return: condition,
          notes,
        })
        .eq('id', assignmentId);

      if (assignError) throw assignError;

      // Update inventory status based on condition
      const newStatus = condition === 'damaged' ? 'damaged' : 'available';
      const { error: updateError } = await api
        .from('rpl_epi_inventory')
        .update({ status: newStatus })
        .eq('id', inventoryId);

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rpl-epi-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['rpl-epi-inventory'] });
      queryClient.invalidateQueries({ queryKey: ['rpl-stats'] });
      toast({ title: 'EPI devuelto correctamente' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

// ============== INCIDENT TYPES ==============

export function useIncidentTypes(activeOnly = false) {
  return useQuery({
    queryKey: ['rpl-incident-types', activeOnly],
    queryFn: async () => {
      let query = api
        .from('rpl_incident_types')
        .select('*')
        .order('name');

      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as IncidentType[];
    },
  });
}

// ============== INCIDENTS ==============

export function useIncidents(filters?: {
  status?: IncidentStatus | 'all';
  severity?: IncidentSeverity | 'all';
}) {
  return useQuery({
    queryKey: ['rpl-incidents', filters],
    queryFn: async () => {
      let query = api
        .from('rpl_incidents_detailed')
        .select('*')
        .order('occurred_at', { ascending: false });

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters?.severity && filters.severity !== 'all') {
        query = query.eq('severity', filters.severity);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as IncidentDetailed[];
    },
  });
}

export function useIncident(id: string | undefined) {
  return useQuery({
    queryKey: ['rpl-incident', id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await api
        .from('rpl_incidents_detailed')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as IncidentDetailed;
    },
    enabled: !!id,
  });
}

export function useCreateIncident() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (data: Partial<Incident>) => {
      const { data: result, error } = await api
        .from('rpl_incidents')
        .insert([{
          ...data,
          reported_by: user?.id,
        }])
        .select()
        .single();

      if (error) throw error;
      return result as Incident;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['rpl-incidents'] });
      queryClient.invalidateQueries({ queryKey: ['rpl-stats'] });
      toast({ title: 'Incidente registrado', description: `Número: ${data.incident_number}` });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

export function useUpdateIncident() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Incident> }) => {
      const { error } = await api
        .from('rpl_incidents')
        .update(data)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rpl-incidents'] });
      queryClient.invalidateQueries({ queryKey: ['rpl-incident'] });
      queryClient.invalidateQueries({ queryKey: ['rpl-stats'] });
      toast({ title: 'Incidente actualizado' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

export function useCloseIncident() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      root_cause,
      corrective_actions,
      preventive_actions,
    }: {
      id: string;
      root_cause: string;
      corrective_actions: string;
      preventive_actions?: string;
    }) => {
      const { error } = await api
        .from('rpl_incidents')
        .update({
          status: 'closed',
          closed_at: new Date().toISOString(),
          root_cause,
          corrective_actions,
          preventive_actions,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rpl-incidents'] });
      queryClient.invalidateQueries({ queryKey: ['rpl-incident'] });
      queryClient.invalidateQueries({ queryKey: ['rpl-stats'] });
      toast({ title: 'Incidente cerrado' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

// ============== TRAINING TYPES ==============

export function useTrainingTypes(activeOnly = false) {
  return useQuery({
    queryKey: ['rpl-training-types', activeOnly],
    queryFn: async () => {
      let query = api
        .from('rpl_training_types')
        .select('*')
        .order('name');

      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as TrainingType[];
    },
  });
}

export function useCreateTrainingType() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: Partial<TrainingType>) => {
      const { data: result, error } = await api
        .from('rpl_training_types')
        .insert([data])
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rpl-training-types'] });
      toast({ title: 'Tipo de formación creado' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

// ============== TRAINING SESSIONS ==============

export function useTrainingSessions(filters?: { status?: TrainingStatus | 'all'; upcoming?: boolean }) {
  return useQuery({
    queryKey: ['rpl-training-sessions', filters],
    queryFn: async () => {
      let query = api
        .from('rpl_training_sessions')
        .select('*, training_type:rpl_training_types(*)')
        .order('scheduled_date', { ascending: true });

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters?.upcoming) {
        query = query.gte('scheduled_date', new Date().toISOString().split('T')[0]);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as TrainingSession[];
    },
  });
}

export function useCreateTrainingSession() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (data: Partial<TrainingSession>) => {
      const { data: result, error } = await api
        .from('rpl_training_sessions')
        .insert([{
          ...data,
          created_by: user?.id,
        }])
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rpl-training-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['rpl-stats'] });
      toast({ title: 'Sesión de formación creada' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

export function useUpdateTrainingSession() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TrainingSession> }) => {
      const { error } = await api
        .from('rpl_training_sessions')
        .update(data)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rpl-training-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['rpl-stats'] });
      toast({ title: 'Sesión actualizada' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

// ============== TRAINING ATTENDEES ==============

export function useTrainingAttendees(sessionId: string | undefined) {
  return useQuery({
    queryKey: ['rpl-training-attendees', sessionId],
    queryFn: async () => {
      if (!sessionId) return [];

      const { data, error } = await api
        .from('rpl_training_attendees')
        .select('*, employee:profiles(id, full_name)')
        .eq('session_id', sessionId)
        .order('created_at');

      if (error) throw error;
      return data as TrainingAttendee[];
    },
    enabled: !!sessionId,
  });
}

export function useCertificationsExpiring() {
  return useQuery({
    queryKey: ['rpl-certifications-expiring'],
    queryFn: async () => {
      const { data, error } = await api
        .from('rpl_certifications_expiring')
        .select('*')
        .order('days_until_expiry');

      if (error) throw error;
      return data as CertificationExpiring[];
    },
  });
}

export function useRegisterAttendance() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: {
      session_id: string;
      employee_id: string;
      attendance_status?: AttendanceStatus;
    }) => {
      const { error } = await api
        .from('rpl_training_attendees')
        .insert([data]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rpl-training-attendees'] });
      toast({ title: 'Asistente registrado' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

export function useUpdateAttendance() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<TrainingAttendee>;
    }) => {
      const { error } = await api
        .from('rpl_training_attendees')
        .update(data)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rpl-training-attendees'] });
      queryClient.invalidateQueries({ queryKey: ['rpl-certifications-expiring'] });
      toast({ title: 'Asistencia actualizada' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}
